import { useCallback, useEffect, useRef, useState } from "react";
import {
  AgentReplyPreview,
  findAgentReplyPreviews,
  findLatestFloatingAgentSeenEventAt,
  isForeignForegroundReply,
} from "./floating-agent-session-events";
import { playPeekNotificationSound } from "./floating-agent-sounds";
import { MAX_VISIBLE_PEEK_BUBBLES, PEEK_SOUND_COOLDOWN_MS } from "./floating-agent-constants";
import {
  FloatingAgentPageStore,
  isNotifiableSessionEvent,
} from "@/store/pages/FloatingAgentPageStore/FloatingAgentPageStore";
import { clientEnvModule } from "@/modules/client-env";
import { AppStore } from "@/store";

/**
 * Agent-reply bookkeeping shared by the widget hosts: the synced last-seen
 * watermark, the unseen/peek-queue derivation, per-bubble dismissals, and
 * the peek-arrival sound. Dismissing a bubble hides it from the stack but
 * keeps the unread dot; only opening the panel marks replies seen.
 *
 * The watermark is the session's synced `owner_last_seen_event_at`, so the
 * dot and bubbles clear across devices; the ding stays per-live-device via
 * a local already-dinged set.
 */
export const useFloatingAgentReplies = ({
  store,
  floatingAgentPage,
  isPanelOpen,
  isFocused,
}: {
  store: AppStore;
  floatingAgentPage: FloatingAgentPageStore;
  isPanelOpen: boolean;
  isFocused: boolean;
}): {
  hasUnread: boolean;
  peekQueue: AgentReplyPreview[];
  markRepliesSeen: () => void;
  dismissPeekBubble: (eventId: string) => void;
  dismissAllPeeks: () => void;
} => {
  const [dismissedReplyIds, setDismissedReplyIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingSeenEventAt, setPendingSeenEventAt] = useState<string | null>(null);

  // Synced cross-device watermark; a missing session or null field means
  // no replies have been seen yet.
  const syncedLastSeenReplyAt = floatingAgentPage.session?.ownerLastSeenEventAt ?? "";
  const lastSeenReplyAt =
    pendingSeenEventAt && pendingSeenEventAt > syncedLastSeenReplyAt
      ? pendingSeenEventAt
      : syncedLastSeenReplyAt;

  useEffect(() => {
    if (!pendingSeenEventAt) return;
    if (!floatingAgentPage.sessionId || syncedLastSeenReplyAt >= pendingSeenEventAt) {
      setPendingSeenEventAt(null);
    }
  }, [floatingAgentPage.sessionId, pendingSeenEventAt, syncedLastSeenReplyAt]);

  // Identifies this install to the notification-origin check. Null until the
  // cache read lands, which reads as "notify", matching pre-origin behavior.
  const [localClientGroupId, setLocalClientGroupId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void clientEnvModule.clientGroupId().then(({ clientGroupId }) => {
      if (!cancelled) setLocalClientGroupId(clientGroupId);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // A reply answering another install's message is that install's business:
  // dropping it here silences all three surfaces at once. The watermark scan in
  // markRepliesSeen deliberately keeps reading the unfiltered list.
  const notifiableEvents = floatingAgentPage.sessionEvents.filter(
    event => !isForeignForegroundReply(event, localClientGroupId)
  );

  // Any notifiable event newer than the last-seen watermark keeps the unread
  // state active; only text-bearing replies/system notices become peek bubbles.
  // Thinking steps and digests never count.
  const replyPreviews = findAgentReplyPreviews(notifiableEvents);
  const hasUnread = notifiableEvents.some(
    event =>
      isNotifiableSessionEvent(event) &&
      event.locally_created_at > lastSeenReplyAt &&
      !event.is_partial
  );
  const unseenReplies = replyPreviews.filter(reply => reply.createdAt > lastSeenReplyAt);
  const peekQueue = unseenReplies
    .filter(reply => !dismissedReplyIds.has(reply.eventId))
    .slice(-MAX_VISIBLE_PEEK_BUBBLES);
  const hasVisibleReplyPreviews = peekQueue.length > 0;

  useEffect(() => {
    store.floatingAgent.setHasVisibleReplyPreviews(hasVisibleReplyPreviews);
    return () => store.floatingAgent.setHasVisibleReplyPreviews(false);
  }, [hasVisibleReplyPreviews, store.floatingAgent]);

  // Kept in a ref so the stable markRepliesSeen callback reads the live focus
  // value without re-creating on every focus flip.
  const isFocusedRef = useRef(isFocused);
  isFocusedRef.current = isFocused;

  const markRepliesSeen = useCallback(() => {
    // The synced watermark is cross-device; only the focused window may
    // advance it, so a backgrounded tab or an unfocused floating window can't
    // clear the unread state out from under the active one.
    if (!isFocusedRef.current) return;
    const latestSeenEventAt = findLatestFloatingAgentSeenEventAt(floatingAgentPage.sessionEvents);
    const sessionId = floatingAgentPage.sessionId;
    // Only advance; a stale dispatch would move the watermark backwards for
    // every device.
    if (latestSeenEventAt && sessionId && latestSeenEventAt > lastSeenReplyAt) {
      setPendingSeenEventAt(latestSeenEventAt);
      void store.sessions.markSessionSeen(sessionId, latestSeenEventAt);
    }
    setDismissedReplyIds(new Set());
  }, [floatingAgentPage, lastSeenReplyAt, store]);

  const dismissPeekBubble = useCallback((eventId: string) => {
    setDismissedReplyIds(current => {
      const next = new Set(current);
      next.add(eventId);
      return next;
    });
  }, []);

  // Collapse-time bulk dismiss: whatever was on screen doesn't immediately
  // re-pop; the unread dot stays until the panel is opened.
  const peekQueueRef = useRef(peekQueue);
  peekQueueRef.current = peekQueue;
  const dismissAllPeeks = useCallback(() => {
    setDismissedReplyIds(current => {
      const next = new Set(current);
      for (const reply of peekQueueRef.current) next.add(reply.eventId);
      return next;
    });
  }, []);

  // Replies arriving while the panel is open in the focused window are read as
  // they arrive. Replies that land while unfocused stay unread until focus
  // returns, at which point this effect re-runs and marks them seen.
  useEffect(() => {
    if (isPanelOpen && isFocused && hasUnread) {
      markRepliesSeen();
    }
  }, [isPanelOpen, isFocused, hasUnread, markRepliesSeen]);

  // Ding on reply arrival, once per live device. Sourced from arrivals — not
  // the peek queue — so a remote watermark advance clearing the dot cannot
  // swallow the local cue. Only replies created after this window mounted
  // count: session events stream in asynchronously, so boot restore of
  // pre-existing replies stays silent. The cooldown collapses a multi-message
  // burst into the first message's ding. Arrivals into a focused open panel are
  // read as they land, so they stay silent; an unfocused open panel does not
  // mark them read, so they still ding.
  const mountedAtRef = useRef(new Date().toISOString());
  const dingedEventIdsRef = useRef<Set<string>>(new Set());
  const lastPeekSoundAtRef = useRef(0);
  const replyPreviewsRef = useRef(replyPreviews);
  replyPreviewsRef.current = replyPreviews;
  const replyIdsKey = replyPreviews.map(reply => reply.eventId).join(",");
  useEffect(() => {
    const dinged = dingedEventIdsRef.current;
    const freshReplies = replyPreviewsRef.current.filter(
      reply => !dinged.has(reply.eventId) && reply.createdAt > mountedAtRef.current
    );
    for (const reply of replyPreviewsRef.current) dinged.add(reply.eventId);
    if (freshReplies.length === 0) return;
    if (isPanelOpen && isFocused) return;
    const now = Date.now();
    if (now - lastPeekSoundAtRef.current < PEEK_SOUND_COOLDOWN_MS) return;
    lastPeekSoundAtRef.current = now;
    playPeekNotificationSound();
  }, [replyIdsKey, isPanelOpen, isFocused]);

  return {
    hasUnread,
    peekQueue,
    markRepliesSeen,
    dismissPeekBubble,
    dismissAllPeeks,
  };
};
